import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';
import { CategorySpentData } from '../../utils/chartsUtils';
import { getCategoryTextColor } from './categoryUtils';
import { CardWithHeader } from '../ui/CardWithHeader';
import { ExpandModal } from '../ui/ExpandModal';
import { NoDataInPeriod } from '../ui/NoDataInPeriod';
import { cn } from '../../lib/utils';
import { tertiaryText } from '../../styling/stylingHelper';

ChartJS.register(ArcElement, Tooltip, Legend);

interface SpendByCategoryDonutProps {
  data: CategorySpentData[];
}

/**
 * All-restaurants "Spend by category" donut. Generalises the chart.js doughnut setup used
 * by RestaurantRevenuePieChart for cross-restaurant category spend, coloured via
 * `getCategoryTextColor`. Compact card + maximize → ExpandModal (bigger donut + full legend).
 */
export const SpendByCategoryDonut: React.FC<SpendByCategoryDonutProps> = ({ data }) => {
  const { t } = useTranslation();
  const [maximized, setMaximized] = useState(false);

  const colors = data.map(item => getCategoryTextColor(item.categoryKey));

  const chartData: ChartData<'doughnut'> = {
    labels: data.map(item => item.category),
    datasets: [
      {
        data: data.map(item => item.percentage),
        backgroundColor: colors,
        borderWidth: 0,
        borderRadius: 10,
        spacing: 6,
        hoverOffset: 2,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context) => {
          let tooltipEl = document.getElementById('order-category-spend-chartjs-tooltip');

          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'order-category-spend-chartjs-tooltip';
            tooltipEl.innerHTML = `
              <div class="bg-white dark:bg-card-dark border border-[#F5F5F5] dark:border-borderGrey-dark rounded-lg p-2 shadow-lg">
                <span class="tooltip-text"></span>
              </div>
            `;
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          if (tooltipModel.body) {
            const textSpan = tooltipEl.querySelector('.tooltip-text') as HTMLElement;
            const dataIndex = tooltipModel.dataPoints[0].dataIndex;
            const item = data[dataIndex];
            textSpan.innerHTML = `${item.category} ${item.percentage.toFixed(1)}%`;
            textSpan.style.color = colors[dataIndex];
          }

          const position = context.chart.canvas.getBoundingClientRect();
          tooltipEl.style.opacity = '1';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX - 30 + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - 40 + 'px';
          tooltipEl.style.pointerEvents = 'none';
          tooltipEl.style.zIndex = '100';
          tooltipEl.style.transition = 'all 0.1s ease';
          tooltipEl.style.transform = 'translateX(-50%)';
        },
      },
    },
  };

  // Chart + legend body, reused inline and (larger) inside the maximize modal. The legend is
  // capped in height and scrolls so a long category list doesn't make the card grow unbounded.
  const renderBody = (heightClass: string, legendMaxWidthClass: string, legendMaxHeightClass: string) => {
    if (data.length === 0) {
      return <NoDataInPeriod />;
    }
    return (
      <div className="w-full">
        <div className={cn('relative w-full flex items-center justify-center select-none mt-6', heightClass)}>
          <Doughnut data={chartData} options={options} />
        </div>

        <div className={cn('mt-6 mb-3 flex items-center flex-wrap justify-center mx-auto gap-3 overflow-y-auto custom-scroll', legendMaxWidthClass, legendMaxHeightClass)}>
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-[3px]"
                style={{ backgroundColor: colors[index] }}
              />
              <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 font-medium whitespace-nowrap">
                {item.category} ({item.percentage.toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const maximizeButton = (
    <button
      type="button"
      onClick={() => setMaximized(true)}
      title={t('dashboard.maximize')}
      aria-label={t('dashboard.maximize')}
      className={cn('p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition', tertiaryText)}
    >
      <Maximize2 className="w-4 h-4" />
    </button>
  );

  return (
    <>
      <CardWithHeader
        title={t('orderHistory.spendByCategory')}
        headerActionsRight={maximizeButton}
      >
        {renderBody('h-[200px] sm:h-[240px]', 'max-w-[424px]', 'max-h-[180px]')}
      </CardWithHeader>

      <ExpandModal
        isOpen={maximized}
        onClose={() => setMaximized(false)}
        title={t('orderHistory.spendByCategory')}
        maxWidth="max-w-2xl"
      >
        {renderBody('h-[360px] sm:h-[440px]', 'max-w-full', 'max-h-[260px]')}
      </ExpandModal>
    </>
  );
};

export default SpendByCategoryDonut;
